// ─── FICHE d'une ligne PRÉVUE (lecture) ───────────────────────────
// Le tap informe, le swipe → modifie (principe app-wide). Une ligne prévue
// n'avait pas de fiche : `FicheOccurrence` décrit une ligne POUR UN MOIS,
// alors qu'ici on est hors mois — c'est la configuration.
//
// Elle répond à « qu'est-ce que cette prévision a produit, et que va-t-elle
// produire ? » (Cédric, 11/08) :
//   • les échéances PASSÉES avec leur statut et l'opération qui les honore —
//     la « croix verte » demandée, mais déclinée : une échéance peut être
//     honorée EN PARTIE, un seul signe ne suffirait pas ;
//   • les échéances à venir, sur un HORIZON que l'utilisateur règle ;
//   • le terme de la ligne, ou ce qu'elle engage par an quand elle n'en a pas.
import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Feuille, SectionPliable, Stepper, frJMA, useUI, useT } from '@mop/core';
import { montantOperation, formatEuro, dateEffective } from './services/calculs';
import { moisCourant, historiqueLigne, moyenneReelle } from './services/occurrences';
import { rattachementsDOccurrence } from './services/repartition';
import { emojiCategorie, cleCategorie } from './data/categories';
import { statutDe, estClos } from './data/statuts';
import ArbreEcheances from './ArbreEcheances';


// L'horizon se règle au compteur ± avec un bouton d'unité tournant (Cédric,
// 13/08) : ni échelle imposée, ni clavier à sortir. Le plafond borne la
// projection — au-delà de 10 ans elle ne veut plus rien dire, et surtout
// `moisEntre` y boucle sur autant d'itérations.
const PLAFOND_MOIS = 120;

const libelleMois = (mk, t) => {
  const [y, m] = String(mk || '').split('-').map(Number);
  const noms = t('commun.moisLongs');
  return `${(Array.isArray(noms) ? noms[m - 1] : '') || mk || ''} ${y || ''}`.trim();
};

export default function FicheLignePrevue({
  visible, ligne, actuals = [], rattachements = [], exceptions = [], nomBudget = '',
  onFermer, onEditer, onRattacherEcheance, onRattacherLot,
  // Les actions sur les FEUILLES de l'arbre (les operations rattachees).
  onDetacherPart, onEditerOperation, onDeplacerPart, onFicheOperation,
  onToutDetacher, onVerrouiller,   // sur le MOIS
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  // ⚠️ Tous les hooks AVANT le moindre retour conditionnel (règle React).
  // `duree.moisExact` FAIT FOI (ce que lit `historiqueLigne`) ; `unite` ne
  // choisit que l'AFFICHAGE. Changer d'unité CONVERTIT (17/08, même motif
  // que la Durée d'un crédit dans `SectionCredit.js`) : 2 ans affichés
  // doivent redevenir 24 en repassant en mois, pas se réinterpréter.
  const [duree, setDuree] = useState({ moisExact: 1, unite: 'mois' });   // 1 mois (Cédric, 13/08)
  const horizon = Math.min(PLAFOND_MOIS, duree.moisExact || 12);
  const dureeAffichee = duree.unite === 'annee'
    ? Math.round((duree.moisExact / 12) * 100) / 100
    : duree.moisExact;
  const dureeIncr = (sens) => {
    if (duree.unite === 'mois') {
      setDuree(d => ({ ...d, moisExact: Math.max(1, Math.min(PLAFOND_MOIS, d.moisExact + sens)) }));
    } else {
      // Un cran = UNE année pleine depuis l'année ARRONDIE courante — la
      // virgule éventuelle (ex. 26 mois → 2,17 ans) disparaît ici, comme
      // pour la Durée d'un crédit. Repasser en mois montre le compte exact.
      const anneesRondes = Math.max(1, Math.round(duree.moisExact / 12) + sens);
      setDuree(d => ({ ...d, moisExact: Math.min(PLAFOND_MOIS, anneesRondes * 12) }));
    }
  };
  const basculerUniteDuree = () => setDuree(d => ({ ...d, unite: d.unite === 'annee' ? 'mois' : 'annee' }));
  const [passeOuvert, setPasseOuvert] = useState(false);

  const l = ligne || {};
  const h = useMemo(
    () => historiqueLigne({ ligne, actuals, exceptions, rattachements, horizon }),
    [ligne, actuals, exceptions, rattachements, horizon],
  );
  // Le libellé de l'opération qui honore une échéance : c'est le lien concret
  // entre la prévision et le compte, celui que Cédric cherchait.
  // Les OPERATIONS qui honorent un mois — objets et non libelles concatenes :
  // ce sont les feuilles de l'arbre, chacune porte sa date et SA PART (le
  // montant du rattachement, pas celui de l'operation entiere : une operation
  // peut etre partagee entre plusieurs previsions).
  const operationsDe = (mois) => rattachementsDOccurrence(rattachements, l.id, mois)
    .map((r) => {
      const op = actuals.find(x => x.id === r.depenseId);
      return {
        id: r.id,               // du RATTACHEMENT : c'est lui qu'on detache
        depenseId: r.depenseId,
        mois: r.mois,
        operation: op || null,  // pour ouvrir sa fiche ou l'editer
        libelle: op?.libelle || '',
        date: op ? frJMA(dateEffective(op)) : '',
        montant: r.montant,
      };
    })
    .filter(x => x.libelle || x.montant);

  if (!visible || !ligne) return null;
  const revenu = l.sens === 'revenu';
  // Le decompte suit la regle du vert : ce qui est CLOS, pas ce qui tombe juste.
  const honorees = h.passees.filter(estClos).length;
  // Ce que la prevision a REELLEMENT coute (ou rapporte) en moyenne. Le prevu
  // est une intention ronde ; la moyenne dit si elle est bien calibree.
  const moyenne = moyenneReelle(h.passees);

  return (
    <Feuille visible={visible} titre={l.libelle || t(cleCategorie(l.categorie))}
      emoji={emojiCategorie(l.categorie)} hauteur={0.9} onFermer={onFermer}
      action={{ icone: '✏️', label: t('finances.actModifierLigne'), onPress: onEditer }}
      /* §2 — bouton Rattacher dans la barre de titre. `boutons` est la prop
         prévue par `Feuille` pour ça (vérifiée, pas supposée) : elle rend le
         contenu dans l'en-tête, entre l'action et la croix.
         C'est l'entrée du rattachement en LOT : plusieurs opérations d'un
         coup, contrairement au 🔗 de chaque échéance qui n'en vise qu'une. */
      boutons={onRattacherLot ? (
        <TouchableOpacity onPress={onRattacherLot} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          accessibilityLabel={t('finances.rattacherOps')}>
          <Text style={{ fontSize: 18, marginRight: 14 }}>🔗</Text>
        </TouchableOpacity>
      ) : null}>

      <View style={s.bandeau}>
        <Text style={[s.montant, revenu && { color: UI.ok || '#2e7d32' }]}>
          {montantOperation(l.montant, l.sens)}
        </Text>
        {/* La moyenne du REEL sous le prevu (Cedric, 14/08) : l'ecart se
            lisait ligne a ligne sans jamais se totaliser. Absente quand rien
            n'a ete constate — un zero serait une affirmation fausse. */}
        {moyenne.nb > 0 && (
          <Text style={s.moyenne}>
            {t('finances.reelMoyen')} {formatEuro(moyenne.montant)}
            <Text style={s.moyenneNb}>  ·  {t('finances.surNMois', { n: moyenne.nb })}</Text>
            {Math.abs(moyenne.ecart) >= 0.01 && (() => {
              // Convention signée (15/08) : positif = favorable, couleur
              // directe — le signe est porté par le sens de la ligne.
              const e = (revenu ? 1 : -1) * moyenne.ecart;
              return (
                <Text style={[s.moyenneEcart,
                  e > 0 ? { color: UI.ok || '#2e7d32' } : { color: UI.danger || '#c0392b' }]}>
                  {'  ·  '}{e > 0 ? '+' : ''}{formatEuro(e)}
                </Text>
              );
            })()}
          </Text>
        )}
        <Text style={s.sous}>
          {t(`finances.${l.frequence === 'ponctuelle' ? 'ponctuelle' : 'recurrente'}`)}
          {l.frequence && !['mensuelle', 'ponctuelle'].includes(l.frequence) ? `  ·  ${l.frequence}` : ''}
          {nomBudget ? `  ·  💰 ${nomBudget}` : ''}
          {l.actif === false ? `  ·  ${t('finances.nd_inactive')}` : ''}
        </Text>

        {/* La PERIODE de validite et l'inflation (Cedric, 15/08). Elles
            expliquent pourquoi une prevision n'apparait pas tel mois, et
            pourquoi son montant bouge d'une annee sur l'autre — deux
            questions qu'on se posait en regardant l'arbre sans avoir la
            reponse sous les yeux.
            « Sans fin » est la NORME pour un loyer ou un salaire : on
            l'ecrit, plutot que de laisser un blanc qui ressemble a un oubli. */}
        <Text style={s.periode}>
          {l.debut ? t('finances.periodeDepuis', { mois: libelleMois(l.debut, t) }) : ''}
          {l.fin
            ? `  ·  ${t('finances.periodeJusqua', { mois: libelleMois(l.fin, t) })}`
            : `  ·  ${t('finances.sansFin').toLowerCase()}`}
          {Number(l.inflationTaux) > 0
            ? `  ·  ${t('finances.inflationCourt')} ${l.inflationTaux} %`
            : ''}
        </Text>
      </View>

      {/* PASSÉ — replié par défaut : l'écran s'ouvre sur ce qui vient, pas sur
          ce qui est fait. Le titre porte le décompte, donc on sait ce qu'il y a
          derrière sans l'ouvrir. */}
      {h.passees.length > 0 && (
        <SectionPliable emoji="🕘" titre={t('finances.echeancesPassees')}
          sousTitre={`${honorees}/${h.passees.length}${h.tronquees ? `  ·  +${h.tronquees}` : ''}`}
          ouvert={passeOuvert} onToggle={() => setPasseOuvert(v => !v)}>
          {/* Ce qui est tronqué est DIT : cacher en silence serait pire. */}
          {h.tronquees > 0 && (
            <Text style={s.tronque}>⋯ {t('finances.echeancesAnciennes', { n: h.tronquees })}</Text>
          )}
          {/* ARBRE annee → mois (Cedric, 14/08) : une prevision mensuelle
              sans fin produit une liste sans fin. L'annee est le repere
              naturel, et chaque niveau porte son prevu / reel / ecart. */}
          <ArbreEcheances occurrences={h.passees} revenu={revenu}
            operationsDe={operationsDe} onEcheance={onRattacherEcheance}
            onDetacher={onDetacherPart} onEditerOperation={onEditerOperation}
            onDeplacer={onDeplacerPart} onOperation={onFicheOperation}
            onToutDetacher={onToutDetacher} onVerrouiller={onVerrouiller} />
        </SectionPliable>
      )}

      <Text style={s.titre}>{t('finances.prochainesEcheances')}</Text>
      {/* [−] n [＋] [Mois ⇄] (Cédric, 13/08). Le bouton OK a disparu avec la
          saisie clavier : un cran ± applique tout de suite, il n'y a plus
          d'état intermédiaire à valider. */}
      <View style={s.horizon}>
        <Stepper valeur={String(dureeAffichee).replace('.', ',')}
          onMoins={() => dureeIncr(-1)} onPlus={() => dureeIncr(1)} />
        <TouchableOpacity style={s.uniteBtn} activeOpacity={0.7} onPress={basculerUniteDuree}>
          <Text style={s.uniteBtnTxt}>{t(duree.unite === 'annee' ? 'commun.annee' : 'commun.mois')}</Text>
          <Text style={s.uniteRot}>⇄</Text>
        </TouchableOpacity>
      </View>

      <View style={{ height: 8 }} />
      {h.aVenir.length === 0 ? (
        <Text style={s.vide}>{t('finances.aucuneOpPeriode', { periode: libelleMois(moisCourant(), t) })}</Text>
      ) : (
        /* MEME arbre que les echeances passees (Cedric, 14/08) : deux
           presentations pour la meme chose divergeraient au premier
           ajustement. Les mois a venir n'ont pas d'operation rattachee, donc
           pas de feuille — l'arbre s'arrete naturellement au mois. */
        <ArbreEcheances occurrences={h.aVenir} revenu={revenu}
          operationsDe={operationsDe} onEcheance={onRattacherEcheance}
          onDetacher={onDetacherPart} onEditerOperation={onEditerOperation}
          onDeplacer={onDeplacerPart} onOperation={onFicheOperation}
          onToutDetacher={onToutDetacher} onVerrouiller={onVerrouiller} />
      )}

      {/* Le terme, ou ce que la ligne engage. « Sans fin » seul ne dirait rien
          de plus que le champ Fin ; le montant annuel, si. */}
      <View style={s.pied}>
        {h.fin ? (
          <>
            <Text style={s.piedLbl}>⏹ {t('finances.fin')}  ·  {libelleMois(h.fin.mois, t)}</Text>
            <Text style={s.piedVal}>{formatEuro(h.fin.prevu)}</Text>
          </>
        ) : (
          <>
            <Text style={s.piedLbl}>∞ {t('finances.sansFin')}</Text>
            {h.parAn > 0 && <Text style={s.piedVal}>{formatEuro(h.parAn)} / {t('finances.annee').toLowerCase()}</Text>}
          </>
        )}
      </View>
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  bandeau: { alignItems: 'center', paddingVertical: 6, marginBottom: 10 },
  montant: { fontSize: 26, fontWeight: '800', color: UI.ink },
  sous: { fontSize: 12.5, color: UI.muted, marginTop: 3, textAlign: 'center' },
  periode: { fontSize: 12, color: UI.faint, marginTop: 3, fontWeight: '700' },
  moyenne: { fontSize: 13, color: UI.ink, fontWeight: '800', marginTop: 4 },
  moyenneNb: { fontSize: 12, color: UI.faint, fontWeight: '700' },
  moyenneEcart: { fontSize: 12.5, fontWeight: '800' },
  titre: { fontSize: 12, color: UI.faint, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3, marginTop: 16, marginBottom: 8 },
  vide: { fontSize: 13, color: UI.faint, lineHeight: 18 },
  tronque: { fontSize: 12, color: UI.faint, marginBottom: 8 },
  ech: { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: UI.card, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 9, paddingHorizontal: 12, marginBottom: 8 },
  echFutur: { borderStyle: 'dashed' },
  echIc: { fontSize: 15, width: 20 },
  echMois: { fontSize: 14, fontWeight: '700', color: UI.ink },
  echQui: { fontSize: 11.5, color: UI.muted, marginTop: 1 },
  echPrevu: { fontSize: 14, fontWeight: '800', color: UI.ink },
  echReel: { fontSize: 11.5, color: UI.muted, marginTop: 1, fontWeight: '700' },
  echLien: { fontSize: 13, color: UI.accent, marginLeft: 8 },
  horizon: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 },
  // Bouton d'unité — mêmes proportions que `SelecteurDuree` (Core), repris
  // localement (comme `SectionCredit.js`) : ici l'unité CONVERTIT la valeur,
  // ce que le composant générique fait volontairement pas.
  uniteBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 14, paddingVertical: 7, borderRadius: 11, borderWidth: 1, borderColor: UI.accent, backgroundColor: UI.card },
  uniteBtnTxt: { fontSize: 14.5, fontWeight: '800', color: UI.accent },
  uniteRot: { fontSize: 12, color: UI.faint, fontWeight: '700' },
  pied: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 6, paddingVertical: 11, paddingHorizontal: 12, borderRadius: 11, borderWidth: 1, borderColor: UI.accent },
  piedLbl: { fontSize: 13.5, fontWeight: '800', color: UI.accent },
  piedVal: { fontSize: 14, fontWeight: '800', color: UI.accent },
});
